import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export const SubscriptionSuccessPage = () => {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const checkStatus = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) { navigate('/'); return; }

      let attempts = 0;
      const poll = async () => {
        try {
          const res = await api.get(`/subscription/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            setStatus('success');
            await refreshUser();
          } else if (attempts < 5) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            setStatus('pending');
          }
        } catch (e) {
          setStatus('error');
        }
      };
      poll();
    };
    checkStatus();
  }, [searchParams, api, refreshUser, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'transparent' }}>
      {status === 'checking' && (
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-sky-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Sprawdzanie płatności...</p>
        </div>
      )}
      {status === 'success' && (
        <div className="text-center animate-scaleIn">
          <div 
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              boxShadow: '0 0 40px rgba(16, 185, 129, 0.4)'
            }}
          >
            <Check className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Subskrypcja aktywna!</h1>
          <p className="text-slate-400 mb-8 text-lg">Masz teraz dostęp do wszystkich treści PRO</p>
          <Button 
            onClick={() => navigate('/lessons')} 
            className="h-14 px-10 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-lg font-semibold"
          >
            Zacznij naukę
          </Button>
        </div>
      )}
      {status === 'pending' && (
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Przetwarzanie płatności</h2>
          <p className="text-slate-400 mb-6">Płatność jest w trakcie weryfikacji</p>
          <Button 
            onClick={() => navigate('/lessons')} 
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-white/10"
          >
            Wróć do aplikacji
          </Button>
        </div>
      )}
      {status === 'error' && (
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Wystąpił błąd</h2>
          <p className="text-slate-400 mb-6">Nie udało się zweryfikować płatności</p>
          <Button 
            onClick={() => navigate('/paywall')} 
            className="bg-sky-500 hover:bg-sky-600"
          >
            Spróbuj ponownie
          </Button>
        </div>
      )}
    </div>
  );
};
